import isNil from "lodash/isNil"
import join from "url-join"
import bus from "~/bus"
import i18n from "~/i18n"
import { notify } from "~/lib/bugsnag"

import { sleep } from "~/lib/utils"

import { version } from "~package"

export class ServerError extends Error {
    constructor(response) {
        super(i18n.global.t("errors.serverError"))
        this.response = response
    }
}

export class ApiError extends Error {
    constructor(response) {
        super(response.errors?.join("\n") ?? response.message)
        this.response = response
    }
}

/**
 * This type of error is thrown when plugins try to access play actions while being in review mode
 */
export class PluginReviewActionNotAllowedError extends Error {
    constructor(response) {
        super(response.errors?.join("\n") ?? response.message)
        this.response = response
    }
}

/**
 * Keeps track of all pending requests made using the context.
 */
export default class AjaxCtx {
    constructor(baseUrl = "", defaults = {}) {
        this.baseUrl = baseUrl
        this.requests = []
        this.defaults = defaults
    }

    /** Fakes navigator.sendBeacon since sendBeacon in chrome is not ready to send JSON data */
    sendBeacon(url, data) {
        // Firefox throws an error if the param of new Request() isn't a valid url
        if ("keepalive" in new Request("http://invent.es")) {
            return this.fetchData(url, {
                method: "POST",
                body: JSON.stringify(data),
                keepalive: true,
            })
        } else {
            const request = new XMLHttpRequest()
            request.withCredentials = this.defaults.credentials === "include"
            const cookedUrl = join(this.baseUrl, url)
            request.open("POST", cookedUrl, false)
            for (const [name, value] of Object.entries(this.defaults.headers)) {
                request.setRequestHeader(name, value)
            }
            request.send(JSON.stringify(data))
        }
    }

    /**
     * Proxies the call to fetch, stores the requested url while it's pending.
     * Include @baseUrl field in @init in order to use a different baseUrl from default one.
     *
     * @throws {TypeError} Network error.
     * @throws {ServerError} When the response status code is 500.
     */
    async fetch(url, init) {
        init = Object.assign({}, this.defaults, init)
        const baseUrl = isNil(init.baseUrl) ? this.baseUrl : init.baseUrl
        url = join(baseUrl, url)
        // waitFor includes the a string with the queries we want to wait for i.e: "/participant-activity/"
        if (init.waitFor) {
            let pendingRequests = this.requests.filter(r =>
                r.url.includes(init.waitFor)
            )
            let waitingTime = 0
            while (pendingRequests.length > 0 && waitingTime < 10000) {
                await sleep(100)
                pendingRequests = this.requests.filter(r =>
                    r.url.includes(init.waitFor)
                )
                waitingTime += 100
            }
        }
        const method = init.method || "GET"
        const request = { url, method }
        request.abortController = new AbortController()

        this.cancelRepeatedRequests(url)
        this.requests.push(request)
        init = { ...init, signal: request.abortController.signal }

        try {
            const response = await fetch(url, init)

            if (response.ok) return response

            if (response.status >= 500) {
                if (response.status === 503) {
                    window.location.assign(
                        `${import.meta.env.VITE_API_BASE_URL}/custom/maintenance/v3/updating.html`
                    )
                } else {
                    throw new ServerError(response)
                }
            } else if (response.status >= 400) {
                switch (response.status) {
                    case 401:
                        {
                            const json = await response.clone().json()
                            bus.emit("auth-error", json.meta)
                        }
                        break

                    case 404:
                        bus.emit("404-error")
                        break

                    case 405:
                        bus.emit("405-error")
                        break

                    case 409:
                        throw new PluginReviewActionNotAllowedError(response)

                    default:
                    // nothing
                }

                return response
            }
            throw new Error("Unexpected response status.")
        } finally {
            this.deleteRequest(url)
        }
    }

    /**
     * Same as fetch + res.json()
     *
     * @throws {TypeError} Network error.
     * @throws {ServerError} When the response status code is 500.
     * @throws {SyntaxError} When the response body is not valid JSON.
     */
    async fetchJSON() {
        const res = await this.fetch(...arguments)
        const json = await res.json()
        if (json.version != version) {
            bus.emit("set-version-outdated")
        }
        return json
    }

    /**
     * Same as fetchJSON + parse OKN API format
     *
     * @throws {TypeError} Network error.
     * @throws {ServerError} When the response status code is 500.
     * @throws {SyntaxError} When the response body is not valid JSON.
     * @throws {ApiError} When the response status is "error".
     */
    async fetchAPI() {
        const res = await this.fetchJSON(...arguments)
        if (res.status == "success") {
            return res
        } else {
            throw new ApiError(res)
        }
    }

    /**
     * Same as fetchAPI but only get the data
     *
     * @throws {TypeError} Network error.
     * @throws {ServerError} When the response status code is 500.
     * @throws {SyntaxError} When the response body is not valid JSON.
     * @throws {ApiError} When the response status is "error".
     */
    fetchData() {
        return this.fetchAPI(...arguments).then(({ data }) => data)
    }

    /**
     * Upload a File or Blob to temporary storage and get a token that identifies it.
     * `progressCallback` will be called with the uploaded and total bytes of
     * the request.
     */
    uploadFile(fileOrBlob, progressCallback, fileName) {
        if (!fileOrBlob) {
            throw new Error("Trying to upload an empty file!")
        }

        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest()
            request.withCredentials = this.defaults.credentials === "include"

            const url = join(this.baseUrl, "upload")
            request.open("POST", url)

            for (const [name, value] of Object.entries(this.defaults.headers)) {
                if (name === "Content-Type") {
                    // Do not include the default Content-Type header.
                    // We are simulating sending a form, and that
                    // requires using the `multipart/form-data` type.
                    //
                    // The code must not set that header, though, as
                    // the big yellow warning says in:
                    // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects
                    continue
                }
                request.setRequestHeader(name, value)
            }

            const form = new FormData()
            form.append("file", fileOrBlob, fileName)

            this.requests.push({ url, method: "POST" })

            request.upload.addEventListener("progress", e => {
                if (progressCallback && e.lengthComputable) {
                    progressCallback(e.loaded, e.total)
                }
            })

            request.addEventListener("load", () => {
                this.deleteRequest(url)
                if (request.status == 200) {
                    const response = JSON.parse(request.response)
                    if (response.status == "success") {
                        resolve(response.data)
                    } else {
                        reject(new ApiError(response))
                    }
                } else {
                    if (request.status == 404) bus.emit("404-error")
                    if (request.status == 401) {
                        const response = JSON.parse(request.response)
                        bus.emit("auth-error", response.meta)
                    }
                    try {
                        //TODO: this is ugly and we should find a better way
                        const response = JSON.parse(request.response)
                        reject(new ApiError(response))
                    } catch (_) {
                        reject(
                            new Error(`${request.status} ${request.statusText}`)
                        )
                    }
                }
            })

            request.addEventListener("error", e => {
                this.deleteRequest(url)
                reject(new Error(`${e}`))
            })

            request.send(form)
        })
    }

    /**
     * Cancel GET requests in this.requests if they were sent to the same @url
     * @param {String} url
     */
    cancelRepeatedRequests(url) {
        const repeatedRequests = this.requests.filter(r => {
            const sameUrl = r.url == url
            const isGetRequest = r.method.toUpperCase() == "GET"
            return sameUrl && isGetRequest
        })

        for (const request of repeatedRequests) {
            this.cancelAndDeleteRequest(request)
        }
    }

    cancelAndDeleteRequest(request) {
        try {
            request.abortController.abort()
        } catch (error) {
            notify(error)
        }
        this.deleteRequest(request.url)
    }

    /**
     * Deletes a request given its url
     * @param {String} url
     */
    deleteRequest(url) {
        const idx = this.requests.findIndex(r => r.url == url)
        if (idx == -1) {
            return
        }
        this.requests.splice(idx, 1)
    }
}

export const defaultCtx = new AjaxCtx(import.meta.env.VITE_API_BASE_URL, {
    credentials: "include",
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Language": import.meta.env.VITE_DEFAULT_LOCALE,
        Parent: document.referrer,
    },
})
